import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('pt-BR');
};

const calcularTerminoPagamento = (dataCompra, parcelasTotais) => {
  if (!dataCompra || !parcelasTotais) return '—';
  const data = new Date(dataCompra + 'T00:00:00');
  data.setMonth(data.getMonth() + parcelasTotais - 1);
  const mes = (data.getMonth() + 1).toString().padStart(2, '0');
  const ano = data.getFullYear();
  return `${mes}/${ano}`;
};

// Botão
export function BotaoCompraParcelada({ onSuccess }) {
  const [showModal, setShowModal] = useState(false);
  const [editingCompra, setEditingCompra] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const data = {
      discriminacao: formData.get('discriminacao'),
      data_compra: formData.get('data_compra'),
      valor_total: parseFloat(formData.get('valor_total')),
      parcelas_totais: parseInt(formData.get('parcelas_totais'))
    };

    try {
      await axios.post(`${API}/financas/compras-parceladas`, data, getAuthHeaders());
      toast.success('Compra parcelada cadastrada');
      setShowModal(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
        Compra Parcelada
      </button>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Nova Compra Parcelada</h2>
                <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Discriminação *</label>
                  <input
                    type="text"
                    name="discriminacao"
                    required
                    placeholder="Ex: TV 50 polegadas"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data da Compra *</label>
                  <input
                    type="date"
                    name="data_compra"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valor da Compra (R$) *</label>
                  <input
                    type="number"
                    name="valor_total"
                    step="0.01"
                    min="0"
                    required
                    placeholder="Ex: 1500.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Parcelas *</label>
                  <input
                    type="number"
                    name="parcelas_totais"
                    min="1"
                    required
                    placeholder="Ex: 12"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
                >
                  Salvar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

// Seção
export function SecaoComprasParceladas({ carregarDados }) {
  const [compras, setCompras] = useState([]);
  const [showSection, setShowSection] = useState(false);
  const [editingParcelas, setEditingParcelas] = useState({});

  useEffect(() => {
    if (showSection) {
      carregarCompras();
    }
  }, [showSection]);

  const carregarCompras = async () => {
    try {
      const response = await axios.get(`${API}/financas/compras-parceladas`, getAuthHeaders());
      setCompras(response.data);
    } catch (error) {
      toast.error('Erro ao carregar');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja excluir?')) return;
    try {
      await axios.delete(`${API}/financas/compras-parceladas/${id}`, getAuthHeaders());
      toast.success('Compra excluída');
      carregarCompras();
      if (carregarDados) carregarDados();
    } catch (error) {
      toast.error('Erro');
    }
  };

  const handleSaveParcelasPagas = async (compraId, value) => {
    try {
      await axios.put(`${API}/financas/compras-parceladas/${compraId}`, {
        parcelas_pagas: parseInt(value) || 0
      }, getAuthHeaders());
      toast.success('Atualizado');
      carregarCompras();
    } catch (error) {
      toast.error('Erro');
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
      <button
        onClick={() => setShowSection(!showSection)}
        className="w-full p-4 flex justify-between items-center hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900">Compras Parceladas</h3>
          <span className="text-sm text-gray-500">({compras.length})</span>
        </div>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${showSection ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showSection && (
        <div className="p-4 border-t border-gray-200">
          {compras.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Nenhuma compra cadastrada</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Discriminação</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tot.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vlr Parcela</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pagas</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rest.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Saldo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Término</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {compras.map((c) => {
                    const vlrParc = c.valor_total / c.parcelas_totais;
                    const rest = c.parcelas_totais - c.parcelas_pagas;
                    const saldo = vlrParc * rest;
                    
                    return (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{c.discriminacao}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(c.data_compra)}</td>
                        <td className="px-4 py-3 text-sm font-medium">{formatCurrency(c.valor_total)}</td>
                        <td className="px-4 py-3 text-sm">{c.parcelas_totais}x</td>
                        <td className="px-4 py-3 text-sm">{formatCurrency(vlrParc)}</td>
                        <td className="px-4 py-3 text-sm">
                          <input
                            type="number"
                            min="0"
                            max={c.parcelas_totais}
                            defaultValue={c.parcelas_pagas}
                            onBlur={(e) => handleSaveParcelasPagas(c.id, e.target.value)}
                            className="w-16 px-2 py-1 border rounded text-sm"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm">{rest}</td>
                        <td className="px-4 py-3 text-sm font-medium text-orange-600">{formatCurrency(saldo)}</td>
                        <td className="px-4 py-3 text-sm">{calcularTerminoPagamento(c.data_compra, c.parcelas_totais)}</td>
                        <td className="px-4 py-3 text-sm">
                          <button onClick={() => handleDelete(c.id)} className="text-red-600 hover:text-red-800 p-1">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
